import { createGraphQLRequest } from "../../api/utils/httpRequest"
import { AuthAccount2, AuthLocationsResponse, AuthOrganization } from "../../graphql/types"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"

export type AuthAccountsAndOrganizationsResponse = {
    authAccounts2: AuthAccount2[]
    authOrganizations: AuthOrganization[]
}

export const fetchAuthAccountsAndOrganizations = (
    session: string,
    userId: string
): Promise<Maybe<AuthAccountsAndOrganizationsResponse>> =>
    createGraphQLRequest({
        headers: { "twbrds-session": session, "twbrds-user-id": userId },
        body: {
            query: `
                query authAccountsAndOrganizations($userId: String!) {
                    authAccounts2(userId: $userId) {
                        id
                        name
                        typeOfBusiness
                        slug
                        domainName
                        importance
                        organizationId
                        userAccountId
                    }
                    authOrganizations(userId: $userId) {
                        id
                        name
                        type
                        originTimestamp
                        lastUpdateTimestamp
                        importance
                        userAccountId                                      
                    }
                }
            `,
            operationName: "authAccountsAndOrganizations",
            variables: {
                userId: userId,
            },
        },
    }).then(response => {
        if (response && response.data && response.data.authAccounts2 && response.data.authOrganizations) {
            return response.data
        }

        return null
    })

export const fetchAuthLocations = async (headers: Record<string, any>): Promise<AuthLocationsResponse> => {
    const response = await createGraphQLRequest({
        headers: headers,
        body: {
            query: `
                query authLocations2 {
                    authLocations2 {
                        locations {
                            id
                            name
                            timezone
                            terminals {
                                id
                                locationId
                                name
                                type
                                prefix
                                url
                                aggregatorType
                                metadata {
                                    onlineOrdering {
                                        status
                                        menuUpdateStatus
                                        priceMultiplier
                                        shouldRoundUpPrices
                                        webOrderingEnabled
                                        mobileOrderingEnabled   
                                        googleOrderingLinkEnabled
                                        images {
                                            locationImageUrl
                                        }                                                                              
                                    }
                                    KDS {
                                        isExpediter
                                    }
                                    POS {
                                        CFD {
                                            isCFDConnectionEnabled
                                            theme {
                                                idleScreen {
                                                    title
                                                    description
                                                    logoUrl
                                                    bgImagesUrls
                                                    bgImagesIntervalMs                                                                                               
                                                }
                                                orderScreen {
                                                    promoImagesUrls
                                                    promoImagesIntervalMs                                                
                                                }
                                            }
                                        }
                                    }
                                    waitlist {
                                        id                
                                        selfEnrollment                
                                        confirmationRequired                
                                        selfEnrollmentAvailability                
                                        gracePeriod                
                                        minPartySize             
                                        maxPartySize
                                    }
                                }
                                aggregatorConfig {
                                    type
                                    uber {
                                        storeId
                                        name
                                    }
                                    email {
                                        address
                                    }
                                    doordash {
                                        address
                                    }
                                    grubhub {
                                        address
                                    }
                                    slice {
                                        address
                                    }
                                    figure {
                                        storeId
                                        storeName
                                        logo
                                        description
                                        images
                                    }
                                    westSideEats {
                                        apiKey
                                    }
                                }
                            }
                        }
                        masterListAccess
                    }
                }
            `,
            operationName: "authLocations2",
            variables: {},
        },
    })
    return {
        locations: response?.data?.authLocations2?.locations ?? [],
        masterListAccess: response?.data?.authLocations2?.masterListAccess || false,
    }
}
